/* eslint-disable indent */
import { useCallback, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useMutation } from 'react-query';
import { Dialog } from 'primereact/dialog';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { toast } from 'layout';
import {
  sendMailFinishMembers,
  sendMailMultipleProjects,
  sendMailRemindMembers,
  sendMailToCouncilMember,
  sendMailToCouncilMemberOfMultiProjects,
} from 'apis/councils.api';
import Loading from 'components/Loading';
import ErrorDialog from 'components/ErrorDialog';
import { HugerteEditor, TextInput } from 'components/FormControl';
import { sendRemindCouncilValidationSchema } from 'features/Council/validation';

import { COUNCIL_EMAIL_TYPES, TOAST } from 'constant';
import ConfirmDialog from 'components/ConfirmDialog';
import { catchError, formatDatetime } from 'utils/func';

dayjs.extend(utc);
dayjs.extend(timezone);

const formOptions = { resolver: yupResolver(sendRemindCouncilValidationSchema) };
export default function SendMailRemindAllMembersDialog({
  visible,
  setVisible,
  council,
  refetch,
  isOneMember,
  recipient,
  hasProjects,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    mutate: mutateSendRemailToAllMembers,
    error,
    isError,
    isLoading,
  } = useMutation(sendMailRemindMembers);

  const { mutateAsync: mutateSendToMemberAsync } = useMutation(
    hasProjects ? sendMailToCouncilMemberOfMultiProjects : sendMailToCouncilMember
  );

  const { mutateAsync: mutateSendEstablishedEmailToAllMemberAsync } = useMutation(
    hasProjects ? sendMailMultipleProjects : sendMailFinishMembers
  );

  const { projectProposalId } = useParams();
  const [isSentEmailLoading, setIsSentEmailLoading] = useState(false);
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmDialog, setIsVisibleConfirmDialog] = useState(false);
  const [emailType, setEmailType] = useState(COUNCIL_EMAIL_TYPES.REMIND);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', ...formOptions });
  const { isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Events
  const handleSendEmailCouncilToMember = async (data) => {
    if (recipient && isOneMember) {
      try {
        setIsSentEmailLoading(true);
        const res = await mutateSendToMemberAsync({
          councilId: council?.id,
          memberId: recipient.id,
          projectProposalId,
          emailType,
          ...(emailType === COUNCIL_EMAIL_TYPES.REMIND && {
            title: data?.title,
            content: data?.content,
          }),
        });

        setValue('content', '');
        setValue('title', '');
        setEmailType(COUNCIL_EMAIL_TYPES.REMIND);
        setIsVisibleDialogError(false);

        if (typeof refetch === 'function') {
          refetch();
        }

        setVisible(false);
        setIsVisibleConfirmDialog(false);
        setTimeout(() => {
          setIsSentEmailLoading(false);
        }, 50);

        if (res.status === 200) {
          toast(TOAST.SUCCESS, t('council.viewList.sendEmailToMemberSuccesfully'));
        } else {
          toast(TOAST.SUCCESS, t('council.viewList.sendEmailToMemberFailed'));
        }
      } catch (sendMailError) {
        catchError(t, sendMailError, navigate);
        setIsSentEmailLoading(false);
      }

      return;
    }

    try {
      setIsSentEmailLoading(true);
      const res = await mutateSendEstablishedEmailToAllMemberAsync({
        councilId: council?.id,
        isSentMail: true,
        isForce: true,
        ...(!hasProjects && { projectProposalId }),
      });
      setIsVisibleDialogError(false);

      if (typeof refetch === 'function') {
        refetch();
      }

      setVisible(false);
      setIsVisibleConfirmDialog(false);
      setTimeout(() => {
        setIsSentEmailLoading(false);
      }, 50);

      if (res.status === 200) {
        toast(TOAST.SUCCESS, t('council.viewList.sendEstablishCouncilEmailSuccess'));
      } else {
        toast(TOAST.SUCCESS, t('council.viewList.sendEmailToMemberFailed'));
      }
    } catch (sendMailError) {
      catchError(t, sendMailError, navigate);
      setIsSentEmailLoading(false);
    }
  };

  const onReject = () => {
    setIsVisibleConfirmDialog(false);
    setEmailType(COUNCIL_EMAIL_TYPES.REMIND);
  };

  const onSubmit = async (data) => {
    if (isOneMember) {
      return handleSendEmailCouncilToMember(data);
    }

    return mutateSendRemailToAllMembers(
      {
        projectProposalId,
        councilId: council?.id,
        title: data?.title,
        content: data?.content,
      },
      {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('council.member.confirm.success'));
          setValue('content', '');
          setValue('title', '');
          setIsVisibleDialogError(false);

          if (typeof refetch === 'function') {
            refetch();
          }

          setVisible(false);
        },
        onError: () => {
          setIsVisibleDialogError(true);
        },
      }
    );
  };

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const renderLastSentMailAt = useCallback(() => {
    if (isOneMember) {
      return formatDatetime(recipient?.lastMailSentAt, 'HH:mm:ss DD/MM/YYYY');
    }

    return formatDatetime(
      council?.projectProposalCouncils?.[0]?.lastSentMail,
      'HH:mm:ss DD/MM/YYYY'
    );
  }, [recipient, council, isOneMember]);

  // #endregion Events

  return (
    <>
      {isLoading && <Loading />}
      {isError && (
        <ErrorDialog
          title={t('council.viewList.remindMembers.titleRemindMembersError')}
          isError={isError}
          error={error}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}

      <Dialog
        header={
          isOneMember
            ? `${t('council.viewList.remindMembers.oneMemberHeader')}: ${
                recipient?.contact?.fullname
              }`
            : t('council.viewList.remindMembers.header')
        }
        visible={visible}
        onHide={() => setVisible(false)}
        draggable={false}
        className="w-full sm:w-8 lg:w-6"
      >
        <div className="flex ml-3 flex-column relative pr-8">
          <div className="mb-2">
            <span className="font-semibold">
              {t(`council.fields.name.${council?.councilType?.code}`)}:{' '}
            </span>
            <span>{council?.name}</span>
          </div>
          <div className="mb-2">
            <span className="font-semibold">{t('council.fields.numberSentMail')}: </span>
            <span>
              {isOneMember
                ? recipient?.mailSentCount
                : council?.projectProposalCouncils?.[0]?.numberSentMail ?? 0}
            </span>
          </div>
          <div className="mb-2">
            <span className="font-semibold">{t('council.fields.lastSentMail')}: </span>
            <span>{renderLastSentMailAt()}</span>
          </div>

          <Button
            className="bg-green-500 border-green-500 hover:bg-green-700 absolute right-0"
            style={{ top: '5px' }}
            icon="pi pi-envelope"
            severity="info"
            tooltip={t(
              `council.review.${
                isOneMember ? 'sendEmailToCouncilMember' : 'sendEstablishedEmailToAllMembers'
              }`
            )}
            tooltipOptions={{ position: 'left' }}
            onClick={() => {
              setIsVisibleConfirmDialog(true);
              setEmailType(COUNCIL_EMAIL_TYPES.ESTABLISH_COUNCIL);
            }}
          />
        </div>

        <form className="p-fluid relative ml-3" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <div>
              <TextInput
                label={t('council.viewList.remindMembers.title')}
                isRequired
                name="title"
                control={control}
                errors={errors}
              />
            </div>
            <div>
              <HugerteEditor
                label={t('council.viewList.remindMembers.content')}
                isRequired
                name="content"
                isFontWeightLabel
                height="200px"
                control={control}
                errors={errors}
              />
            </div>
            <div className="flex flex-wrap justify-content-end mt-8">
              <Button
                label={t('formLayout.action.close')}
                severity="danger"
                type="button"
                onClick={() => setVisible(false)}
                className="mr-2 w-8rem"
              />
              <Button
                label={t('formLayout.action.sendMail')}
                severity="info"
                type="submit"
                onClick={checkValidation}
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
                className="w-9rem"
                loading={isLoading || isSentEmailLoading}
              />
            </div>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        content={
          <p>
            {isOneMember ? (
              <>
                {t('council.review.confirmToSendEmailToMember')}:{' '}
                <b>{recipient?.contact?.fullname}</b>{' '}
              </>
            ) : (
              <>
                {t('council.review.confirmToSendEmailToAllMembers')}{' '}
                <b>{t('council.review.confirmToSendEmailToAllMembersNote')}</b>
              </>
            )}
          </p>
        }
        visible={isVisibleConfirmDialog}
        setVisible={setIsVisibleConfirmDialog}
        onReject={onReject}
        onHide={onReject}
        onAccept={handleSendEmailCouncilToMember}
        loading={isLoading || isSentEmailLoading}
      />
    </>
  );
}

SendMailRemindAllMembersDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  council: PropTypes.object,
  isOneMember: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  recipient: PropTypes.object,
  hasProjects: PropTypes.bool,
};

SendMailRemindAllMembersDialog.defaultProps = {
  council: {},
  refetch: () => {},
  isOneMember: false,
  recipient: {},
  hasProjects: false,
};
